<template>
<main class="form w-100 m-auto overflow-hidden mel-glass-panel">
  <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors, isSubmitting }" class="m-2">
    <div class="h3 mb-3 fw-normal">
      <img alt="Icon" src="/icon.svg" class="icon" />
      {{ $t('page.reset-password.heading') }}
    </div>
    <p>{{ $t('page.reset-password.description') }}</p>

    <div class="form-floating mb-3">
      <Field id="email" name="email" type="email" class="form-control" placeholder="name@example.com" disabled :value="routeEmail" />
      <label for="email">{{ $t('field.generic.email') }}</label>
    </div>

    <div class="form-floating">
      <Field id="password" name="password" type="password" autocomplete="new-password" class="form-control" placeholder="Password" :class="{ 'is-invalid': errors.password }" />
      <label for="password">{{ $t('field.generic.password') }}</label>
    </div>
    <div class="form-floating mb-3">
      <Field id="confirmPassword" name="confirmPassword" type="password" autocomplete="new-password" class="form-control" placeholder="Confirm Password" :class="{ 'is-invalid': errors.confirmPassword }" />
      <label for="confirmPassword">{{ $t('page.reset-password.confirmPassword') }}</label>
      <div class="invalid-feedback"><ErrorMessage name="password" /></div>
      <div class="invalid-feedback"><ErrorMessage name="confirmPassword" /></div>
    </div>

    <div class="btn-group w-100">
      <button class="btn btn-primary w-100 py-2" type="submit" :disabled="isSubmitting">
        <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
        {{ $t('page.reset-password.submit') }}
      </button>
      <RouterLink to="/" class="btn btn-outline-secondary py-2" :disabled="isSubmitting">
        {{ $t('action.cancel') }}
      </RouterLink>
    </div>
    <div v-if="errors.apiError" class="alert alert-danger mt-3 mb-0">{{ errors.apiError }}</div>
  </Form>
</main>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import { Form, Field, GenericObject, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import { useAuthStore } from '@/stores';
import { toast } from 'vue3-toastify';
import { useI18n } from 'vue-i18n';
import { router } from "@/services/router.ts";

const route = useRoute();
const i18n = useI18n();

const hashSearchParams = new URLSearchParams(route.hash.replace(/^#/, ""));
const hashParams = Object.fromEntries(hashSearchParams.entries());
const routeEmail = hashParams.email as string;
const routeToken = hashParams.token as string;

const schema = Yup.object().shape({
  password: Yup.string().required(i18n.t('validation.passwordRequired')).min(6, i18n.t('validation.minLength', { min: 6 })),
  confirmPassword: Yup.string().required(i18n.t('validation.confirmPasswordRequired')).oneOf([Yup.ref('password')], i18n.t('validation.passwordConfirmMatch'))
});

async function onSubmit(values: GenericObject, { setErrors }: GenericObject) {
  const authStore = useAuthStore();
  const { password } = values;

  try {
    var response = await authStore.resetPassword(routeEmail, routeToken, password);
    if (response.status === 200 && response.data) {
      toast.success(i18n.t('page.reset-password.resetSuccess'), { autoClose: 3000 });
      setTimeout(() => {
        router.push({ name: 'home' });
      }, 3000);
    }
    else {
      toast.error(i18n.t('page.reset-password.resetError'), { autoClose: 10000 });
    }
  } catch (error) {
    return setErrors({ apiError: error });
  }
}
</script>

<style scoped>
.form {
  max-width: 640px;
  padding: 1rem;
  transition: all 0.4s ease 0s;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

@media (min-width: 768px) {
  .form {
    left: calc(50% + 280px);
    transform: translate(calc(-50% - 140px), -50%);
  }
}

.form .form-floating:focus-within {
  z-index: 2;
}

.form input[name="password"] {
  margin-bottom: -1px;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.form input[name="confirmPassword"] {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
